"use client";

import { useTranslations } from "next-intl";

import { buttonVariants } from "@/components/shadcn/button";
import { TwitterX } from "@/icons/twitter-x";
import { cn } from "cn";
import { Link2 } from "lucide-react";
import { HTMLAttributes } from "react";
import {
    EmailIcon,
    EmailShareButton,
    FacebookIcon,
    FacebookShareButton,
    LinkedinIcon,
    LinkedinShareButton,
    TelegramIcon,
    TelegramShareButton,
    TwitterShareButton,
    WhatsappIcon,
    WhatsappShareButton,
} from "react-share";

interface ShareButtonsProps {
    shareUrl: string;
}

export default function ShareButtons({
    shareUrl,
    className,
    ...props
}: HTMLAttributes<HTMLDivElement> & ShareButtonsProps) {
    const t = useTranslations("news");

    const handleCopyLink = () =>
        navigator.clipboard.writeText(shareUrl).then(
            () => {
                alert(t("copied"));
            },
            () => {
                alert(t("copyFailed"));
            },
        );

    const shareButtonClassName =
        "opacity-80 grayscale transition-opacity hover:opacity-100 hover:grayscale-0";

    return (
        <div {...props} className={cn("flex flex-col gap-3", className)}>
            <span className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
                {t("share")}
            </span>
            <div className="flex flex-wrap gap-2">
                <FacebookShareButton
                    url={shareUrl}
                    className={shareButtonClassName}
                >
                    <FacebookIcon size={36} round={true} />
                </FacebookShareButton>
                <TwitterShareButton
                    url={shareUrl}
                    className={shareButtonClassName}
                >
                    <TwitterX width={36} height={36} />
                </TwitterShareButton>
                <WhatsappShareButton
                    url={shareUrl}
                    className={shareButtonClassName}
                >
                    <WhatsappIcon size={36} round={true} />
                </WhatsappShareButton>
                <LinkedinShareButton
                    url={shareUrl}
                    className={shareButtonClassName}
                >
                    <LinkedinIcon size={36} round={true} />
                </LinkedinShareButton>
                <TelegramShareButton
                    url={shareUrl}
                    className={shareButtonClassName}
                >
                    <TelegramIcon size={36} round={true} />
                </TelegramShareButton>
                <EmailShareButton
                    url={shareUrl}
                    className={shareButtonClassName}
                >
                    <EmailIcon size={36} round={true} />
                </EmailShareButton>
            </div>
            <button
                onClick={handleCopyLink}
                className={cn(
                    buttonVariants({ variant: "outline", size: "sm" }),
                    "self-start",
                )}
            >
                <Link2 />
                {t("copyLink")}
            </button>
        </div>
    );
}
